import { create } from 'zustand';
import { User } from '../types/user';
import { auth } from '../lib/firebase';
import { cookieService } from '../utils/cookieService';

interface AuthState {
  user: User | null;
  isLoading: boolean;
  setUser: (user: User | null) => void;
  setLoading: (loading: boolean) => void;
  isAdmin: () => boolean;
  isSuperAdmin: () => boolean;
  isCouncilMember: () => boolean;
  clearAuth: () => Promise<void>;
  persistUser: (user: User) => void;
  restoreUser: () => User | null;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  isLoading: true,
  setUser: (user) => {
    set({ user });
    if (user) {
      get().persistUser(user);
    } else {
      cookieService.removeAuthCookie();
    }
  },
  setLoading: (loading) => set({ isLoading: loading }),
  isAdmin: () => {
    const { user } = get();
    return user?.role === 'admin' || user?.role === 'superadmin';
  },
  isSuperAdmin: () => {
    const { user } = get();
    return user?.role === 'superadmin';
  },
  isCouncilMember: () => {
    const { user } = get();
    return user?.role === 'council' || get().isAdmin();
  },
  clearAuth: async () => {
    try {
      if (auth.currentUser) {
        await auth.signOut();
      }
    } catch (error) {
      console.error('Error signing out:', error);
    } finally {
      cookieService.removeAuthCookie();
      set({ user: null, isLoading: false });
    }
  },
  persistUser: (user) => {
    cookieService.setAuthCookie(user.id, {
      role: user.role,
      councilRole: user.councilRole
    });
  },
  restoreUser: () => {
    const authData = cookieService.getAuthCookie();
    if (!authData) return null;
    return get().user;
  }
}));